import { useEffect, useMemo, useState } from 'react';
import { LoadingState, Skeleton, SplitRow, FilterControl, useToast, writeWithToast } from '../common';
import { occupancyByDate } from '../bookings/domain';
import { isoDate } from '../../lib/dates';
import { useWindowedBookings } from '../bookings/useWindowedBookings';
import { api } from '../../lib/api';
import { useStaff } from './useStaff';
import { useShifts } from './useShifts';
import { useHolidays } from './useHolidays';
import { usePayMarks } from './usePayMarks';
import { MonthView } from './MonthView';
import { WeekMoneyStrip } from './WeekMoneyStrip';
import { RotaShiftAlerts } from './ShiftAlertsCard';
import { StaffModal } from './StaffModal';
import { RosterStatStrip } from './RosterStatStrip';
import { monthGridWeeks, monthIsoOf } from './week';
import type { Holiday } from './types';

interface RotaViewProps {
  anchor: string; // ISO date in the shown month
  // A one-shot jump: the day another page sent us to (On-duty "view all"). Scrolls
  // that day's week into view once, then the page clears it. Null on a plain open —
  // Rota must open at the top.
  jumpTo: string | null;
  onJumpDone: () => void;
}

// The Rota body — one month page, laid out as its Fri–Thu weeks (the week is the
// operational unit). The roster grid carries leave overlaid, and each week-block
// ends in its own hours-&-wages strip.
//
// There is no Wages LENS any more. It was a whole second view of the same weeks,
// and it forced a choice: you could see who was working, or you could see what they
// were owed, never both. The strip collapses that — closed it still answers (hours ·
// wage · outstanding), and it opens only when you want to ACT (mark a helper paid).
// Nothing replaces the tabs in the toolbar: a month roll-up there would only repeat
// itself — the stat strip above already carries the month's hours, and each week's
// money lives on that week's own strip.
export function RotaView({
  anchor,
  jumpTo,
  onJumpDone,
}: RotaViewProps) {
  const toast = useToast();
  const { state, reload: reloadStaff } = useStaff();
  const [minimumWage, setMinimumWage] = useState(14.15); // sensible default until /settings answers
  const [filterStaffId, setFilterStaffId] = useState<string | null>(null);
  const [editingLeave, setEditingLeave] = useState<Holiday | null>(null);
  const today = isoDate(new Date());
  const monthIso = monthIsoOf(anchor);

  useEffect(() => {
    api<{ minimumWage: number }>('/settings')
      .then((b) => setMinimumWage(b.data.minimumWage))
      .catch(() => {});
  }, []);

  // The month's Fri–Thu weeks, the fetch window covering them, and the in-month
  // dates the roster stats summarise (so a spill week doesn't skew them).
  const weeks = useMemo(() => monthGridWeeks(monthIso), [monthIso]);
  const all = useMemo(() => weeks.flat(), [weeks]);
  const from = all[0]!;
  const to = all[all.length - 1]!;
  const statDates = useMemo(() => all.filter((d) => monthIsoOf(d) === monthIso), [all, monthIso]);

  const { shifts, assign, remove } = useShifts(from, to);
  const { holidays, reload: reloadHolidays } = useHolidays(from, to);
  const { marks, reload: reloadMarks } = usePayMarks(from, to);
  const { bookings } = useWindowedBookings({ from, to });
  const dogsByDate = useMemo(() => occupancyByDate(bookings, all), [bookings, all]);

  const staff = state.phase === 'ready' ? state.staff : [];

  // No month-level pay maths here any more. Each WeekMoneyStrip computes its own
  // week's rows from the same shifts/marks it is handed — so the money is worked out
  // exactly where it is shown, and this view is back to being about the roster.

  const onTogglePaid = (staffId: string, weekStart: string, paid: boolean) => {
    void writeWithToast(toast, {
      write: async () => {
        await (paid
          ? api(`/pay-marks?staffId=${staffId}&weekStart=${weekStart}`, { method: 'DELETE' })
          : api('/pay-marks', { method: 'PUT', body: { staffId, weekStart } }));
        reloadMarks();
      },
      success: paid ? 'Marked as unpaid' : 'Marked as paid',
      error: 'Could not update payment.',
      revert: reloadMarks,
    });
  };

  const closeModal = () => {
    setEditingLeave(null);
  };
  const onModalChanged = () => {
    reloadStaff();
    reloadHolidays();
  };

  if (state.phase === 'loading') {
    return (
      <LoadingState className="staff-loading" label="Loading rota…">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} variant="row" />
        ))}
      </LoadingState>
    );
  }
  if (state.phase === 'error') {
    return (
      <div className="empty-state pad-30">
        Could not load the rota.
      </div>
    );
  }

  const modalOpen = !!editingLeave;

  return (
    <div className="rota-view">
      <RosterStatStrip shifts={shifts} dogsByDate={dogsByDate} dates={statDates} helpers={staff.length} period="month" />

      {/* The lens tabs are gone and nothing replaces them. A month roll-up here would
          only repeat itself: the stat strip above already carries the month's hours,
          and every week-block's own strip carries that week's money. */}
      <SplitRow
        left={<RotaShiftAlerts />} /* who will be told when the rota changes — read-only strip */
        right={
          <FilterControl
            label="Staff"
            icon="clock"
            mode="single"
            options={staff.map((s) => ({ key: s.id, label: s.name, avatar: { name: s.name, id: s.id } }))}
            selected={filterStaffId ? [filterStaffId] : []}
            onChange={(keys) => setFilterStaffId(keys[0] ?? null)}
          />
        }
      />

      <MonthView
        monthIso={monthIso}
        today={today}
        staff={staff}
        shifts={shifts}
        dogsByDate={dogsByDate}
        holidays={holidays}
        filterStaffId={filterStaffId}
        onAssign={assign}
        onRemove={remove}
        onEditLeave={setEditingLeave}
        scrollToIso={anchor}
        jumpTo={jumpTo}
        onJumpDone={onJumpDone}
        renderWeekFoot={(week) => (
          <WeekMoneyStrip
            weekDates={week}
            staff={staff}
            shifts={shifts}
            minimumWage={minimumWage}
            marks={marks}
            filterStaffId={filterStaffId}
            onToggle={onTogglePaid}
          />
        )}
      />

      {modalOpen && (
        <StaffModal
          staff={staff}
          today={today}
          holiday={editingLeave}
          onClose={closeModal}
          onChanged={onModalChanged}
        />
      )}
    </div>
  );
}
